import { API_CONFIG } from '@/config/api';
import { APIError } from './errorHandler';
import { withRetry } from './retry';
import {
  SessionResponse,
  SessionDetailResponse,
  SessionStats,
  SystemStatusResponse,
  DocumentStatusResponse,
  DocumentListResponse,
  DocumentViewResponse,
  HealthResponse,
  UpdateSessionRequest,
  StreamingEvent,
  InputFilesListResponse,
  RawFileViewResponse,
  ProcessedFileResponse,
  FileMappingResponse,
  ClearAllSessionsResponse,
  FolderNode,
  CreateFolderResponse,
  FolderContentsResponse,
  DocumentUploadResponse,
  InputStatusResponse,
  CheckExistingRequest,
  CheckExistingResponse,
  DeleteDocumentResponse,
  ProcessNewResponse,
  RetryFailedResponse,
  FewShotStatsResponse,
  ConsistencyCheckResponse,
} from '@/types/api';
import {
  FewShotExample,
  CreateExampleRequest,
  UpdateExampleRequest,
  ExampleListFilters,
  ExampleSearchRequest,
  ExampleSearchResponse,
  ExampleStatistics,
  BulkCreateRequest,
  BulkCreateResponse,
  RecommendationRequest,
  RecommendationResponse,
  TestRetrievalRequest,
  TestRetrievalResponse,
  DeleteResponse,
} from '@/types/fewShot';

class APIClient {
  private baseURL: string;
  private getToken: () => Promise<string | null>;

  constructor(getToken: () => Promise<string | null>) {
    this.baseURL = API_CONFIG.BASE_URL;
    this.getToken = getToken;
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = await this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit,
    skipRetry = false,
    customTimeout?: number
  ): Promise<T> {
    const makeRequest = async () => {
      const controller = new AbortController();
      const timeout = customTimeout ?? API_CONFIG.REQUEST_TIMEOUT;
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        const headers = await this.getAuthHeaders();
        const response = await fetch(`${this.baseURL}${endpoint}`, {
          ...options,
          headers: { ...headers, ...options?.headers },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw await APIError.fromResponse(response);
        }

        // Handle empty responses
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.warn(`API ${endpoint} - Non-JSON response, content-type: ${contentType}`);
          return {} as T;
        }

        const result = await response.json();
        return result;
      } catch (error) {
        clearTimeout(timeoutId);
        
        // Handle abort errors
        if (error instanceof Error && error.name === 'AbortError') {
          throw new APIError(408, 'Request timeout', 'TIMEOUT');
        }
        
        throw error;
      }
    };

    // Use retry wrapper unless explicitly skipped
    if (skipRetry) {
      return makeRequest();
    }
    
    return withRetry(makeRequest, {
      maxAttempts: API_CONFIG.RETRY_ATTEMPTS,
      retryableStatuses: [429, 502, 503, 504]
    });
  }

  private async createStreamingConnection(
    endpoint: string,
    options?: RequestInit
  ): Promise<{
    stream: ReadableStream<StreamingEvent>;
    close: () => void;
  }> {
    // STEP 1: Create abort controller for cleanup
    const controller = new AbortController();

    // STEP 2: Get auth headers and send POST request to streaming endpoint
    const headers = await this.getAuthHeaders();
    // This initiates the SSE stream with the backend
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers: { ...headers, ...options?.headers },
      signal: controller.signal
    });

    // STEP 3: Check response status
    if (!response.ok) {
      console.error('Streaming connection failed:', response.status, response.statusText);
      throw await APIError.fromResponse(response);
    }

    // STEP 4: Verify we have a response body
    if (!response.body) {
      throw new Error('No response body for streaming connection');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    // STEP 5: Create ReadableStream that parses SSE events
    const stream = new ReadableStream<StreamingEvent>({
      async start(streamController) {
        let buffer = '';
        
        try {
          // STEP 6: Read chunks from response body
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
                    break;
            }
            
            // STEP 7: Decode chunk and parse SSE events
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;
            
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer
            
            // STEP 8: Process each complete line
            for (const line of lines) {
              // SSE format: "data: {json}\n"
              if (line.startsWith('data: ')) {
                try {
                  const jsonStr = line.slice(6);
                  const eventData = JSON.parse(jsonStr);
                  streamController.enqueue(eventData as StreamingEvent);
                } catch (error) {
                  console.error('❌ SSE PARSE ERROR:', {
                    error: error,
                    line: line.slice(0, 200) + (line.length > 200 ? '...' : ''),
                    lineLength: line.length
                  });
                }
              } else if (line.trim() && !line.startsWith(': ')) {
                // Non-data SSE line received
              }
            }
          }
        } catch (error) {
          if (error instanceof Error && error.name !== 'AbortError') {
            streamController.error(error);
          }
        } finally {
          streamController.close();
        }
      }
    });

    return {
      stream,
      close: () => {
        controller.abort();
        reader.cancel();
      }
    };
  }

  // Chat endpoints
  chat = {
    streamRequest: async (message: string, sessionId: string, attachments?: Array<{data_url: string}>) => {
      // Return a promise that resolves to an EventSource-like object
      // Since EventSource doesn't support POST with custom headers, we'll use a custom implementation
      const body: {
        message: string;
        session_id: string;
        include_debug: boolean;
        stream_tool_calls: boolean;
        attachments?: Array<{data_url: string}>;
      } = {
        message,
        session_id: sessionId,
        include_debug: true,
        stream_tool_calls: true
      };

      if (attachments && attachments.length > 0) {
        body.attachments = attachments;
      }

      return this.createStreamingConnection('/api/chat/stream', {
        method: 'POST',
        body: JSON.stringify(body)
      });
    },
  };

  // Session endpoints
  sessions = {
    list: () => 
      this.request<SessionResponse[]>('/api/sessions'),
    
    create: () => 
      this.request<SessionResponse>('/api/sessions', { 
        method: 'POST' 
      }),
    
    get: (id: string) => 
      this.request<SessionDetailResponse>(`/api/sessions/${id}`),
    
    update: (id: string, updates: UpdateSessionRequest) => 
      this.request<{ message: string; session_id: string; title?: string }>(
        `/api/sessions/${id}`, 
        { 
          method: 'PUT',
          body: JSON.stringify(updates)
        }
      ),
    
    delete: (id: string) => 
      this.request<{ message: string }>(`/api/sessions/${id}`, { 
        method: 'DELETE' 
      }),
    
    stats: (id: string) =>
      this.request<SessionStats>(`/api/sessions/${id}/stats`),

    // Cleanup and maintenance endpoints
    cleanup: (olderThanDays?: number, includeTest = true) =>
      this.request<{
        message: string;
        deleted_old: number;
        deleted_test: number;
        total_deleted: number;
      }>(`/api/sessions/cleanup?older_than_days=${olderThanDays || 30}&include_test=${includeTest}`, {
        method: 'DELETE'
      }),

    clearAll: (confirmToken: string) =>
      this.request<ClearAllSessionsResponse>(`/api/sessions/clear-all?confirm=${confirmToken}`, {
        method: 'DELETE'
      }),


    vacuum: () =>
      this.request<{
        message: string;
        size_before_bytes: number;
        size_after_bytes: number;
        space_saved_bytes: number;
        space_saved_mb: number;
        vacuum_completed: boolean;
      }>('/api/sessions/vacuum', {
        method: 'POST'
      }),

    statistics: () =>
      this.request<{
        total_sessions: number;
        total_messages: number;
        context_messages: number;
        total_tokens: number;
        context_tokens: number;
        database_size_mb: number;
        max_sessions_limit: number;
        retention_days: number;
        context_window_size: number;
        database_path: string;
      }>('/api/sessions/statistics'),
  };

  // System endpoints
  system = {
    health: () => 
      this.request<HealthResponse>('/api/health', undefined, true),
    
    status: () => 
      this.request<SystemStatusResponse>('/api/system/status'),
  };

  // Document endpoints
  documents = {
    status: () =>
      this.request<DocumentStatusResponse>('/api/documents/status'),

    list: () =>
      this.request<DocumentListResponse>('/api/documents/list'),

    view: (filename: string) =>
      this.request<DocumentViewResponse>(`/api/documents/view/${filename}`),

    ingest: (forceReprocess = false) =>
      this.request<{ status: string; task_id: string; message: string }>(
        '/api/documents/ingest',
        {
          method: 'POST',
          body: JSON.stringify({ force_reprocess: forceReprocess })
        }
      ),

    reprocess: () =>
      this.request<{ status: string; task_id: string; message: string }>(
        '/api/documents/reprocess',
        { method: 'POST' }
      ),

    processNew: () =>
      this.request<ProcessNewResponse>(
        '/api/documents/process-new',
        { method: 'POST' }
      ),

    retryFailed: () =>
      this.request<RetryFailedResponse>(
        '/api/documents/retry-failed',
        { method: 'POST' }
      ),

    rebuildVdb: (mode: 'documents' | 'few-shot' | 'all') =>
      this.request<{ status: string; task_id: string; mode: string; message: string }>(
        `/api/documents/rebuild-vdb?mode=${mode}`,
        { method: 'POST' }
      ),

    getRebuildTaskStatus: (taskId: string) =>
      this.request<{
        task_id: string;
        status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
        progress: number;
        current_operation: string | null;
        operations_completed: number;
        operations_total: number;
        mode: string;
        started_at: string;
        completed_at?: string;
        error?: string;
        results?: Record<string, unknown>;
      }>(`/api/documents/rebuild-vdb/tasks/${taskId}`),

    cancelRebuildTask: (taskId: string) =>
      this.request<{ message: string; task_id: string; note: string }>(
        `/api/documents/rebuild-vdb/cancel/${taskId}`,
        { method: 'POST' }
      ),

    downloadAll: async () => {
      const headers = await this.getAuthHeaders();
      return fetch(`${this.baseURL}/api/documents/download-all`, {
        headers
      });
    },

    checkConsistency: () =>
      this.request<ConsistencyCheckResponse>('/api/documents/consistency-check'),

    getTaskStatus: (taskId: string) =>
      this.request<{
        task_id: string;
        status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'cancelling';
        progress: number;
        current_file: string | null;
        files_processed: number;
        files_total: number;
        processing_status: string;
        error?: string;
        started_at: string;
        completed_at?: string;
      }>(`/api/documents/tasks/${taskId}`),

    cancelTask: (taskId: string) =>
      this.request<{ message: string; task_id: string; status: string }>(
        `/api/documents/cancel/${taskId}`,
        { method: 'POST' }
      ),
  };

  // File Management endpoints
  files = {
    listInput: (module?: string) =>
      this.request<InputFilesListResponse>(`/api/files/input/list${module ? `?module=${module}` : ''}`),

    viewRaw: (filepath: string) =>
      this.request<RawFileViewResponse>(`/api/files/input/view/${encodeURIComponent(filepath)}`),

    downloadRaw: async (filepath: string) => {
      // For binary files like PDFs, we return the URL for downloading
      const headers = await this.getAuthHeaders();
      return fetch(`${this.baseURL}/api/files/input/download/${encodeURIComponent(filepath)}`, {
        headers
      });
    },

    getProcessed: (filename: string) =>
      this.request<ProcessedFileResponse>(`/api/files/processed/${filename}`),

    getMapping: (inputFilepath: string) =>
      this.request<FileMappingResponse>(`/api/files/mapping/${encodeURIComponent(inputFilepath)}`),

    deleteDocument: (filename: string) =>
      this.request<DeleteDocumentResponse>(`/api/files/documents/${encodeURIComponent(filename)}`, {
        method: 'DELETE'
      }),
  };

  // Few-shot learning endpoints
  fewShot = {
    // Example CRUD operations
    createExample: (data: CreateExampleRequest) =>
      this.request<FewShotExample>('/api/few-shot/examples', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    listExamples: (filters?: ExampleListFilters, skip = 0, limit = 200) => {
      const params = new URLSearchParams();
      if (skip > 0) params.append('skip', skip.toString());
      if (limit !== 200) params.append('limit', limit.toString());
      if (filters?.is_good_example !== undefined) {
        params.append('is_good_example', filters.is_good_example.toString());
      }
      if (filters?.machine_filter) params.append('machine_filter', filters.machine_filter);
      if (filters?.submachine_part) params.append('submachine_part', filters.submachine_part);
      if (filters?.error_code) params.append('error_code', filters.error_code);
      if (filters?.created_by) params.append('created_by', filters.created_by);

      const queryString = params.toString();
      return this.request<FewShotExample[]>(
        `/api/few-shot/examples${queryString ? `?${queryString}` : ''}`
      );
    },

    getExample: (id: string) =>
      this.request<FewShotExample>(`/api/few-shot/examples/${id}`),

    updateExample: (id: string, data: UpdateExampleRequest) =>
      this.request<FewShotExample>(`/api/few-shot/examples/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    deleteExample: (id: string) =>
      this.request<DeleteResponse>(`/api/few-shot/examples/${id}`, {
        method: 'DELETE',
      }),

    // Search and retrieval
    searchExamples: (request: ExampleSearchRequest) =>
      this.request<ExampleSearchResponse>('/api/few-shot/search', {
        method: 'POST',
        body: JSON.stringify(request),
      }),

    // Statistics and analytics
    getStatistics: () =>
      this.request<FewShotStatsResponse>('/api/few-shot/stats'),

    // Bulk operations
    bulkCreateExamples: (request: BulkCreateRequest) =>
      this.request<BulkCreateResponse>('/api/few-shot/examples/bulk', {
        method: 'POST',
        body: JSON.stringify(request),
      }),

    // Recommendations and testing
    getRecommendations: (request: RecommendationRequest) =>
      this.request<RecommendationResponse>('/api/few-shot/recommendations', {
        method: 'POST',
        body: JSON.stringify(request),
      }),

    testRetrieval: (request: TestRetrievalRequest) =>
      this.request<TestRetrievalResponse>('/api/few-shot/test-retrieval', {
        method: 'POST',
        body: JSON.stringify(request),
      }),

    // Category-based retrieval
    getExamplesByCategory: (machinepart: string, errorCode?: string, k = 10) => {
      const params = new URLSearchParams();
      params.append('k', k.toString());
      if (errorCode) params.append('error_code', errorCode);

      return this.request<FewShotExample[]>(
        `/api/few-shot/examples/category/${encodeURIComponent(machinepart)}?${params.toString()}`
      );
    },

    // Direct JSON file access
    listExamplesFromFile: () =>
      this.request<{examples: FewShotExample[], count: number, file_exists: boolean}>('/api/few-shot/from-file'),
  };

  // Upload endpoints
  upload = {
    // Get folder tree structure
    getFolderTree: () =>
      this.request<FolderNode>('/api/upload/input-tree'),

    // Create new folder
    createFolder: (module: string, subpath?: string) =>
      this.request<CreateFolderResponse>('/api/upload/create-folder', {
        method: 'POST',
        body: JSON.stringify({ module, subpath: subpath || '' }),
      }),

    // Delete empty folder
    deleteFolder: (module: string, subpath?: string) => {
      const params = new URLSearchParams();
      params.append('module', module);
      if (subpath) params.append('subpath', subpath);

      return this.request<{message: string; folder_path: string; deleted: boolean}>(
        `/api/upload/delete-folder?${params.toString()}`,
        { method: 'DELETE' }
      );
    },

    // Get folder contents
    getFolderContents: (module: string, subpath?: string) => {
      const params = new URLSearchParams();
      params.append('module', module);
      if (subpath) params.append('subpath', subpath);

      return this.request<FolderContentsResponse>(`/api/upload/folder-contents?${params.toString()}`);
    },

    // Upload documents with folder support
    uploadDocuments: async (
      files: File[],
      options: {
        module: string;
        subpath?: string;
        file_machines?: Record<string, string>;
        onProgress?: (progress: number) => void;
      }
    ) => {
      const formData = new FormData();

      // Add files
      files.forEach(file => {
        formData.append('files', file);
      });

      // Add folder information
      formData.append('module', options.module);
      if (options.subpath) {
        formData.append('subpath', options.subpath);
      }

      // Add file_machines as JSON string if provided
      if (options.file_machines && Object.keys(options.file_machines).length > 0) {
        formData.append('file_machines', JSON.stringify(options.file_machines));
      }

      // Make request with progress tracking
      const controller = new AbortController();

      try {
        // For file uploads, don't set Content-Type header - let browser set multipart/form-data
        const authHeaders = await this.getAuthHeaders();
        const headers: HeadersInit = {};

        // Copy auth header but exclude Content-Type for FormData uploads
        if (authHeaders['Authorization']) {
          headers['Authorization'] = authHeaders['Authorization'];
        }

        const response = await fetch(`${this.baseURL}/api/upload/documents`, {
          method: 'POST',
          headers,
          body: formData,
          signal: controller.signal
        });

        if (!response.ok) {
          throw await APIError.fromResponse(response);
        }

        return await response.json() as DocumentUploadResponse;

      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error('Upload cancelled');
        }
        throw error;
      }
    },

    // Get upload status
    getInputStatus: () =>
      this.request<InputStatusResponse>('/api/upload/input-status'),

    // List input files
    listInputFiles: (module?: string) => {
      const params = new URLSearchParams();
      if (module) params.append('module', module);

      return this.request<InputFilesListResponse>(`/api/upload/files?${params.toString()}`);
    },

    // Check which files already exist
    checkExisting: (request: CheckExistingRequest) =>
      this.request<CheckExistingResponse>('/api/upload/check-existing', {
        method: 'POST',
        body: JSON.stringify(request),
      }),
  };
}

// Export class for context provider
export { APIClient };